import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import axios from "axios";
import API_ENDPOINTS from "config/api";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

export default function AjustesInventario() {
  const [ajustes, setAjustes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [productos, setProductos] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [ajusteForm, setAjusteForm] = useState({
    producto_id: "",
    almacen_id: "",
    tipo_ajuste: "suma",
    cantidad: "",
    stock_anterior: 0,
    stock_nuevo: 0,
    motivo: "",
    observaciones: "",
  });

  const empresaId = localStorage.getItem("empresaId");

  const tiposAjuste = [
    { value: "suma", label: "Suma (Aumentar Stock)" },
    { value: "resta", label: "Resta (Disminuir Stock)" },
    { value: "absoluto", label: "Ajuste Absoluto (Establecer Stock)" },
  ];

  const motivosAjuste = [
    "Inventario físico",
    "Productos dañados",
    "Productos vencidos",
    "Error de conteo",
    "Devolución",
    "Ajuste por diferencia",
    "Otro",
  ];

  useEffect(() => {
    fetchAjustes();
    fetchProductos();
    fetchAlmacenes();
  }, [empresaId]);

  useEffect(() => {
    if (ajusteForm.producto_id && ajusteForm.cantidad) {
      calcularStockNuevo();
    }
  }, [ajusteForm.producto_id, ajusteForm.cantidad, ajusteForm.tipo_ajuste]);

  const fetchAjustes = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.INVENTARIO.AJUSTES, {
        params: { empresa_id: empresaId },
      });
      setAjustes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener ajustes:", error);
      setAjustes([]);
    }
  };

  const fetchProductos = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.INVENTARIO.GENERAL, {
        params: { empresa_id: empresaId },
      });
      setProductos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener productos:", error);
      setProductos([]);
    }
  };

  const fetchAlmacenes = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.ALMACENES, {
        params: { empresa_id: empresaId },
      });
      setAlmacenes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener almacenes:", error);
      setAlmacenes([]);
    }
  };

  const handleOpenDialog = () => {
    setAjusteForm({
      producto_id: "",
      almacen_id: "",
      tipo_ajuste: "suma",
      cantidad: "",
      stock_anterior: 0,
      stock_nuevo: 0,
      motivo: "",
      observaciones: "",
    });
    setProductoSeleccionado(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAjusteForm({ ...ajusteForm, [name]: value });

    if (name === "producto_id") {
      const producto = productos.find((p) => p.id === parseInt(value));
      setProductoSeleccionado(producto);
      setAjusteForm((prev) => ({
        ...prev,
        producto_id: value,
        stock_anterior: producto?.stock_actual || 0,
      }));
    }
  };

  const calcularStockNuevo = () => {
    if (!productoSeleccionado) return;

    const cantidad = parseFloat(ajusteForm.cantidad) || 0;
    const stockAnterior = productoSeleccionado.stock_actual;
    let stockNuevo = stockAnterior;

    switch (ajusteForm.tipo_ajuste) {
      case "suma":
        stockNuevo = stockAnterior + cantidad;
        break;
      case "resta":
        stockNuevo = stockAnterior - cantidad;
        break;
      case "absoluto":
        stockNuevo = cantidad;
        break;
      default:
        break;
    }

    setAjusteForm((prev) => ({
      ...prev,
      stock_anterior: stockAnterior,
      stock_nuevo: stockNuevo,
    }));
  };

  const handleSubmit = async () => {
    // console.log("handleSubmit called", ajusteForm);
    if (!ajusteForm.producto_id || !ajusteForm.almacen_id || !ajusteForm.cantidad) {
      alert("Por favor complete los campos obligatorios: Producto, Almacén y Cantidad");
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...ajusteForm,
        empresa_id: localStorage.getItem("empresaId") || 1,
        // Asegurar que los IDs sean números
        producto_id: parseInt(ajusteForm.producto_id),
        almacen_id: parseInt(ajusteForm.almacen_id),
        cantidad: parseFloat(ajusteForm.cantidad),
      };

      const response = await axios.post(API_ENDPOINTS.INVENTARIO.AJUSTES, data);
      alert("Ajuste registrado exitosamente");
      fetchAjustes();
      fetchProductos();
      handleCloseDialog();
    } catch (error) {
      console.error("Error al registrar ajuste:", error);
      const errorDetail = error.response?.data?.error || "";
      const errorMsg = error.response?.data?.message || error.message;
      alert("Error al registrar el ajuste: " + errorMsg + (errorDetail ? "\nDetalle: " + errorDetail : ""));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { Header: "Fecha", accessor: "fecha", width: "12%", align: "left" },
    { Header: "Producto", accessor: "producto", align: "left" },
    { Header: "Tipo", accessor: "tipo", width: "15%", align: "left" },
    { Header: "Stock Anterior", accessor: "stock_anterior", width: "12%", align: "center" },
    { Header: "Ajuste", accessor: "ajuste", width: "10%", align: "center" },
    { Header: "Stock Nuevo", accessor: "stock_nuevo", width: "12%", align: "center" },
    { Header: "Motivo", accessor: "motivo", width: "15%", align: "left" },
    { Header: "Usuario", accessor: "usuario", width: "12%", align: "left" },
  ];

  const rows = ajustes.map((ajuste) => ({
    fecha: new Date(ajuste.fecha).toLocaleDateString(),
    producto: ajuste.producto?.nombre || "N/A",
    tipo: ajuste.tipo_ajuste,
    stock_anterior: ajuste.stock_anterior,
    ajuste: (
      <MDTypography
        variant="caption"
        color={ajuste.tipo_ajuste === "suma" ? "success" : "error"}
        fontWeight="medium"
      >
        {ajuste.tipo_ajuste === "suma" ? "+" : "-"}
        {ajuste.cantidad}
      </MDTypography>
    ),
    stock_nuevo: ajuste.stock_nuevo,
    motivo: ajuste.motivo,
    usuario: ajuste.usuario?.name || "Sistema",
  }));

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDTypography variant="h6">Ajustes de Inventario</MDTypography>
        <MDButton
          variant="gradient"
          color="warning"
          startIcon={<Icon>tune</Icon>}
          onClick={handleOpenDialog}
        >
          Nuevo Ajuste
        </MDButton>
      </MDBox>

      <Alert severity="info" sx={{ mb: 3 }}>
        Los ajustes de inventario permiten corregir diferencias de stock. Todos los ajustes quedan
        registrados para auditoría.
      </Alert>

      <Card>
        <MDBox>
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={false}
            showTotalEntries={false}
            noEndBorder
          />
        </MDBox>
      </Card>

      {/* Dialog para crear ajuste */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Registrar Ajuste de Inventario</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Producto"
                name="producto_id"
                value={ajusteForm.producto_id}
                onChange={handleInputChange}
                required
              >
                {productos.map((prod) => (
                  <MenuItem key={prod.id} value={prod.id}>
                    {prod.nombre} - Stock Actual: {prod.stock_actual}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Almacén"
                name="almacen_id"
                value={ajusteForm.almacen_id}
                onChange={handleInputChange}
                required
              >
                {almacenes.map((alm) => (
                  <MenuItem key={alm.id} value={alm.id}>
                    {alm.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Tipo de Ajuste"
                name="tipo_ajuste"
                value={ajusteForm.tipo_ajuste}
                onChange={handleInputChange}
                required
              >
                {tiposAjuste.map((tipo) => (
                  <MenuItem key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cantidad"
                name="cantidad"
                type="number"
                value={ajusteForm.cantidad}
                onChange={handleInputChange}
                required
              />
            </Grid>

            {productoSeleccionado && (
              <>
                <Grid item xs={12}>
                  <Alert severity="info">
                    <strong>Stock Anterior:</strong> {ajusteForm.stock_anterior}{" "}
                    {productoSeleccionado.unidad_medida}
                    <br />
                    <strong>Stock Nuevo:</strong> {ajusteForm.stock_nuevo}{" "}
                    {productoSeleccionado.unidad_medida}
                  </Alert>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Motivo"
                name="motivo"
                value={ajusteForm.motivo}
                onChange={handleInputChange}
                required
              >
                {motivosAjuste.map((motivo) => (
                  <MenuItem key={motivo} value={motivo}>
                    {motivo}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observaciones"
                name="observaciones"
                value={ajusteForm.observaciones}
                onChange={handleInputChange}
                multiline
                rows={3}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="warning"
            disabled={loading}
            sx={{ color: "white" }}
          >
            {loading ? "Registrando..." : "Registrar Ajuste"}
          </Button>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
